import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, DownloadIcon, FileTextIcon, HistoryIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { downloadDeliverable, deleteDeliverable, type DeliverableGroup } from '../../lib/portalApi';
import { can } from '../../lib/portalPermissions';
import { usePortalMutation } from '../../hooks/usePortalQuery';
import { track } from '../../lib/analytics';
import { LIVE_API } from '../../lib/config';
import { InlineError } from './QueryState';
import { fileKind, formatBytes, formatDate, formatRelative } from '../../utils/portalFormat';
import type { Deliverable, OrgRole } from '../../types/portal';
interface DeliverableListProps {
  orgId: string;
  role: OrgRole;
  groups: DeliverableGroup[];
  /** Show the project each file belongs to, for the organisation wide library. */
  showProject?: boolean;
  onNewVersion?: (group: DeliverableGroup) => void;
}

/**
 * One row per deliverable, showing its latest version, with earlier versions a
 * click away. Every download asks the server for a fresh, short lived link after
 * the access check, and the server logs it.
 */
export function DeliverableList({
  orgId,
  role,
  groups,
  showProject = false,
  onNewVersion
}: DeliverableListProps) {
  const [open, setOpen] = React.useState<string | null>(null);
  const [issued, setIssued] = React.useState<{
    id: string;
    url: string;
    expiresAt: string;
  } | null>(null);
  const download = usePortalMutation(downloadDeliverable);
  const remove = usePortalMutation(deleteDeliverable);
  const get = async (file: Deliverable) => {
    const result = await download.run(orgId, file.projectId, file.id);
    if (!result) return;
    track('deliverable_downloaded', {
      project: file.projectId,
      file: file.filename
    });
    if (LIVE_API) window.location.assign(result.url);else setIssued({
      id: file.id,
      ...result
    });
  };
  return <div data-id="element-937" data-mp="dhyyzqex5ms4">
      <InlineError error={download.error ?? remove.error} data-id="element-938" data-mp="iepd4875y8aw" />
      <ul className="divide-y divide-line rounded-md border border-line" data-id="element-939" data-mp="iga7nxth9r8r">
        {groups.map(group => {
        const file = group.latest;
        const expanded = open === file.id;
        return <li key={file.id} className="px-4 py-4 sm:px-5" data-id="element-940" data-mp="3qh6sqtdoxniz">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5" data-id="element-941" data-mp="3lovml8scx5hz">
                <FileTextIcon className="hidden h-5 w-5 shrink-0 text-muted sm:block" aria-hidden="true" data-id="element-942" data-mp="3grdjw04xnmif" />
                <div className="min-w-0 flex-1" data-id="element-943" data-mp="3brr3jhtt5q5e">
                  <p className="text-[15px] text-ink" data-id="element-944" data-mp="3bpehotkxivn2">
                    {file.name}
                    <span className="ml-2 rounded-sm bg-charcoal px-1.5 py-0.5 text-[11px] font-semibold text-body" data-id="element-945" data-mp="36r388jyjqn5d">v{file.version}</span>
                  </p>
                  <p className="mt-1 text-[13px] text-muted" data-id="element-946" data-mp="2xshnci5lqw1f">
                    {fileKind(file.mimeType, file.filename)}, {formatBytes(file.sizeBytes)}, {formatRelative(file.createdAt)}
                    {showProject && <>
                        {', '}
                        <Link to={`/portal/orgs/${orgId}/projects/${group.project.id}?tab=deliverables`} className="text-body hover:text-royal hover:underline" data-id="element-947" data-mp="13kdxo2ytnh2c">
                          {group.project.name}
                        </Link>
                      </>}
                  </p>
                  {file.description && <p className="mt-1 text-[13px] text-body" data-id="element-948" data-mp="1i7gus8j8fnp5">{file.description}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2" data-id="element-949" data-mp="kmbazutqxw7p">
                  {group.history.length > 0 && <button type="button" onClick={() => setOpen(expanded ? null : file.id)} aria-expanded={expanded} className="inline-flex h-9 items-center gap-1.5 rounded-sm px-2.5 text-[13px] text-body transition-colors duration-150 hover:bg-panel hover:text-ink" data-id="element-950" data-mp="pkctoen9ftin">
                      <HistoryIcon className="h-4 w-4" aria-hidden="true" data-id="element-951" data-mp="29yn1x8xdelnx" />
                      {group.history.length} earlier
                      <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-952" data-mp="28kveq88nlfyg" />
                    </button>}
                  {onNewVersion && can(role, 'deliverables.upload') && <button type="button" onClick={() => onNewVersion(group)} className="inline-flex h-9 items-center gap-1.5 rounded-sm px-2.5 text-[13px] text-body transition-colors duration-150 hover:bg-panel hover:text-ink" data-id="element-953" data-mp="2dgap2q7gctq4">
                      <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-954" data-mp="319j1y9cyqfen" />
                      New version
                    </button>}
                  {can(role, 'deliverables.delete') && <button type="button" onClick={() => window.confirm(`Remove “${file.name}” and its earlier versions? It can be restored by Cavalla.`) && void remove.run(orgId, file.projectId, file.id)} className="inline-flex h-9 w-9 items-center justify-center rounded-sm text-muted transition-colors duration-150 hover:bg-panel hover:text-[#FFA192]" aria-label={`Remove ${file.name}`} data-id="element-955" data-mp="3a5s59z429y62">
                      <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-956" data-mp="364c1eulkise6" />
                    </button>}
                  <button type="button" onClick={() => void get(file)} disabled={download.pending} className="inline-flex h-9 items-center gap-1.5 rounded-sm bg-gold px-3 text-[13px] font-medium text-charcoal transition-colors duration-150 hover:bg-[#ff7440] disabled:opacity-60" data-id="element-957" data-mp="3avlphqrgxnny">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-958" data-mp="2s4kqj2qcls4e" />
                    Download
                  </button>
                </div>
              </div>

              {expanded && <ul className="mt-4 space-y-2 border-l border-line pl-4 sm:ml-10" data-id="element-959" data-mp="sxngqc9rznw6">
                  {group.history.map(old => <li key={old.id} className="flex flex-wrap items-center justify-between gap-3 text-[13px]" data-id="element-960" data-mp="ox3nvkw8j6fu">
                      <span className="text-muted" data-id="element-961" data-mp="jy8rth99blds">
                        <span className="text-body" data-id="element-962" data-mp="1gmyctgg6ku3z">Version {old.version}</span>, {formatDate(old.createdAt)}, {formatBytes(old.sizeBytes)}
                        {old.description && `, ${old.description}`}
                      </span>
                      <button type="button" onClick={() => void get(old)} className="text-royal hover:underline" data-id="element-963" data-mp="17lh1ebdpxglm">
                        Download v{old.version}
                      </button>
                    </li>)}
                </ul>}

              {issued && (issued.id === file.id || group.history.some(h => h.id === issued.id)) && <p role="status" className="mt-3 break-all rounded-sm bg-charcoal px-3 py-2 text-[12px] text-muted sm:ml-10" data-id="element-964" data-mp="3da98qqujaoso">
                  Preview: a signed link was issued and the download was logged. It expires at{' '}
                  {new Date(issued.expiresAt).toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit'
            })}. {issued.url}
                </p>}
            </li>;
      })}
      </ul>
    </div>;
}